import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnChanges,
    OnInit,
    Renderer2,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {DynamicFormService} from '../../../dynamic-form/dynamic-form.service';
import {BaseFieldComponent} from '../../base/base-field/base-field.component';
import {EventDispatcherService} from '../../../events/event-dispatcher.service';
import {FilterSelectFieldConfig} from '../filter-select-field.config';
import {NgSelectComponent} from '@ng-select/ng-select';
import {untilDestroyed} from '@ngneat/until-destroy';
import {FilterSelectDropdownComponent} from '../dropdown-popup/filter-select-dropdown.component';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {DeviceService} from '@mydocma/core/src/lib/device';


@Component({
    selector: 'mydocma-form-filter-select-field',
    templateUrl: './filter-select-field.component.html',
    styleUrls: ['./filter-select-field.component.scss'],
    providers: []
})
export class FilterSelectFieldComponent extends BaseFieldComponent<FilterSelectFieldConfig> implements AfterViewInit, OnInit, OnChanges {
    public isOpen: boolean;
    public readonly staticCssClass = 'mydocma-select-field';
    @ViewChild('ngSelectRef', {static: false}) ngSelect: NgSelectComponent;
    public customSearchFunc = this.defaultSearch;
    @ViewChild('ref', {static: false}) formFieldContainer: any;


    private elementRef;
    private dropDownPanel: HTMLDivElement;
    private width;
    private boundOnScroll: () => void;

    constructor(
        public readonly dynamicFormService: DynamicFormService,
        public readonly changeDetectorRef: ChangeDetectorRef,
        public readonly eventDispatcher: EventDispatcherService,
        element: ElementRef,
        renderer: Renderer2,
        private readonly matDialog: MatDialog,
        public readonly deviceService: DeviceService
    ) {
        super(dynamicFormService, changeDetectorRef, eventDispatcher, element, renderer);
        this.elementRef = element;
        this.boundOnScroll = this.onScroll.bind(this);
    }

    ngAfterViewInit() {
        this.ngSelect?.openEvent.pipe(untilDestroyed(this))
            .subscribe(value => {
                setTimeout(() => {
                    this.dropDownPanel = this.ngSelect?.dropdownPanel?.contentElementRef?.nativeElement?.parentElement?.parentElement;
                    if (!this.dropDownPanel) return;
                    window.addEventListener('scroll', this.boundOnScroll, true);
                    this.dropDownPanel.classList.add('mydocma-filter-select-dropdown');
                    if (this.fieldWrapper.config?.dropDownPanelClass?.length) {
                        this.dropDownPanel.classList.add(this.fieldWrapper.config?.dropDownPanelClass);
                    }
                    this.updateDropDownProperties();
                    this.adjustDropdownDirectionForIpad();
                })
            })

        this.ngSelect?.closeEvent.pipe(untilDestroyed(this)).subscribe(() => {
            if (this.dropDownPanel) {
                window.removeEventListener('scroll', this.boundOnScroll, true);
                this.dropDownPanel = null
            }
        })
    }

    /**
     * Lifecycle hook that is called after data-bound properties of a directive are initialized.
     */
    public async ngOnInit() {
        super.ngOnInit();
        this.fieldWrapper.onUpdated.subscribe((value) => {
            if (value['openOnInit']) {
                this.ngSelect.open();
            }
        });
        this.customSearchFunc = this.fieldWrapper.config?.customSearch
            ? this.fieldWrapper.config?.customSearch.bind(this) : this.defaultSearch;
        await this.setSelect();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.customSearchFunc = this.fieldWrapper.config?.customSearch
            ? this.fieldWrapper.config?.customSearch.bind(this) : this.defaultSearch;
    }

    public async setSelect() {
        if ((this.fieldWrapper.config as FilterSelectFieldConfig).getSource) {
            this.fieldWrapper.source = await (this.fieldWrapper.config as FilterSelectFieldConfig).getSource();
            return;
        }

        this.fieldWrapper.source = (this.fieldWrapper.config as FilterSelectFieldConfig).source;
    }

    public async onFieldWrapperUpdated(): Promise<void> {
        super.onFieldWrapperUpdated();

        await this.setSelect();
        this.customSearchFunc = this.fieldWrapper.config?.customSearch
            ? this.fieldWrapper.config?.customSearch.bind(this) : this.defaultSearch;
    }

    public  onControlClick() {

        if(!this.fieldWrapper.config.readonly && this.deviceService.isMobil()) {
            (document.activeElement as HTMLElement).blur();
            const dialogRef = this.matDialog.open(FilterSelectDropdownComponent, { data: {fieldWrapper: this.fieldWrapper, selectedItem: this.control.value}, height: '100%', minWidth: '100%', minHeight: '100%', panelClass: 'mydocma-dialog', autoFocus: 'false'});

            dialogRef.afterClosed().subscribe(result => {
                if(!result) return;

                if (result.id !== undefined && result.id !== null) {
                    this.control.setValue(result.id);
                } else {
                    const currentSource = [...this.fieldWrapper.source];
                    currentSource.push({ id: currentSource.length + 1, text: result });
                    this.fieldWrapper.source = currentSource;
                    this.control.setValue(currentSource.length);
                }
            });
        }

        if (this.isOpen) {
            this.isOpen = null;
        }
        super.onControlClick();
    }

    public addTag(tag: string): any {
        if (!this.fieldWrapper.config?.onItemAdded) {
            return tag;
        }
        return this.fieldWrapper.config?.onItemAdded({
            value: tag,
        });
    }

    resize(event) {
        if (this.ngSelect.isOpen) {
            (this.ngSelect.dropdownPanel as any)._updateXPosition();
            this.updateDropDownProperties()
        }
    }

    updateDropDownProperties() {
        if (this.control.value) {
            this.dropDownPanel.style.width = this.formFieldContainer.matFormField._elementRef.nativeElement.getBoundingClientRect().width + 'px'
            this.dropDownPanel.style.left = this.dropDownPanel.getBoundingClientRect().left - 9 + 'px';
        }
    }

    private onScroll(event: any) {
        if (this.ngSelect && this.ngSelect.isOpen) {
            const isScrollingInScrollHost = (event?.target?.className as string)?.indexOf('ng-dropdown-panel-items') > -1 || (event?.target?.localName as string) === 'input';
            if (isScrollingInScrollHost) {
                return;
            }
            this.ngSelect.close();
        }
    }

    preventFocus() {
        (document.activeElement as HTMLElement).blur()
    }

    defaultSearch(term: string, item: any) {
        term = term.toLocaleLowerCase().trimEnd();
        return item.text?.toLocaleLowerCase()?.indexOf(term) > -1;
    };

    isIpad(): boolean {
        return /iPad/.test(navigator.userAgent) || 
            (/Macintosh/.test(navigator.userAgent) && 'ontouchend' in document);
    }

    private adjustDropdownDirectionForIpad() {
        if (!this.isIpad()) return;
        if (!this.dropDownPanel) return;

        const rect = this.elementRef.nativeElement.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        const keyboardHeight = 350;
        const dropdownHeight = this.dropDownPanel.offsetHeight || 300;

        const spaceBelow = windowHeight - rect.bottom - keyboardHeight;
        const spaceAbove = rect.top;

        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
            this.dropDownPanel.style.top = 'auto';
            this.dropDownPanel.style.bottom = `${windowHeight - rect.top}px`;
        }
    }

    get validationErrorIcon(): string | null {
        const errors = this.control?.errors;
        if (errors?.['mydocmaCustomError'] && errors?.['mydocmaCustomErrorIcon']) {
            return errors['mydocmaCustomErrorIcon'];
        }
        return null;
    }

    get hasValidationError(): boolean {
        return this.control?.invalid && this.control?.touched;
    }

    get validationErrorMessage(): string | null {
        const errors = this.control?.errors;
        if (errors?.['mydocmaCustomError']) {
            return errors['mydocmaCustomError'];
        }
        return null;
    }
}
